import InboxDomain, {
  CONVERSATION_SCOPES,
  ConversationScope,
  InboxAccessBits,
  InboxMyAccess,
} from "entities/domain/inbox";
import InboxMembersDomain, {
  InboxGrant,
  InboxPerson,
} from "entities/domain/inbox/inbox-members-domain";
import ManagedInboxDomain, {
  COVERED_COVERAGE,
  InboxArchiveBlocker,
  InboxCoverage,
  InboxCoverageState,
  InboxSourceReference,
  NO_ELIGIBLE_MEMBER_COVERAGE,
  NO_INTERRUPTING_ROUTE_COVERAGE,
} from "entities/domain/inbox/managed-inbox-domain";
import {
  InboxAccessBitsDTO,
  InboxAccessDTO,
  InboxDTO,
} from "entities/dto/InboxDTO";
import {
  InboxArchiveBlockerDTO,
  InboxCoverageDTO,
  InboxGrantDTO,
  InboxMembersDTO,
  InboxPersonDTO,
  InboxSourceReferenceDTO,
  ManagedInboxDTO,
} from "entities/dto/ManagedInboxDTO";

const COVERAGE_STATES: InboxCoverageState[] = [
  NO_ELIGIBLE_MEMBER_COVERAGE,
  NO_INTERRUPTING_ROUTE_COVERAGE,
  COVERED_COVERAGE,
];

const isInboxCoverageState = (state: string): state is InboxCoverageState =>
  COVERAGE_STATES.some((known) => known === state);

export const isConversationScope = (
  value: unknown
): value is ConversationScope =>
  CONVERSATION_SCOPES.some((scope) => scope === value);

const inboxAccessBitsTransformFromDtoToDomain = (
  data: InboxAccessBitsDTO
): InboxAccessBits => ({
  others: data.others === true,
  unassigned: data.unassigned === true,
});

// A scope this client does not know yet reads as no grant: the capabilities
// still come from `effective`, which the server computed.
export const inboxAccessTransformFromDtoToDomain = (
  data: InboxAccessDTO | null | undefined
): InboxMyAccess | null =>
  data?.inherited && data.effective
    ? {
        grantScope: isConversationScope(data.grant_scope)
          ? data.grant_scope
          : null,
        inherited: inboxAccessBitsTransformFromDtoToDomain(data.inherited),
        effective: inboxAccessBitsTransformFromDtoToDomain(data.effective),
      }
    : null;

export const inboxTransformFromDtoToDomain = (data: InboxDTO): InboxDomain =>
  new InboxDomain(
    data.id,
    data.key,
    data.name,
    data.is_active,
    inboxAccessTransformFromDtoToDomain(data.my_access)
  );

export const inboxSourceReferenceTransformFromDtoToDomain = (
  data: InboxSourceReferenceDTO
): InboxSourceReference => ({
  kind: data.kind,
  dealershipId: data.dealership_id ?? null,
  dealershipName: data.dealership_name ?? null,
  emailHandlerId: data.email_handler_id ?? null,
  email: data.email ?? null,
  source: data.source ?? null,
  leadId: data.lead_id ?? null,
  dealId: data.deal_id ?? null,
  decidedAt: data.decided_at ?? null,
});

export const inboxArchiveBlockerTransformFromDtoToDomain = (
  data: InboxArchiveBlockerDTO
): InboxArchiveBlocker => ({
  code: data.code,
  message: data.message,
  openConversationCount: data.open_conversation_count ?? null,
  closedConversationCount: data.closed_conversation_count ?? null,
  sourceReferences: (data.source_references ?? []).map(
    inboxSourceReferenceTransformFromDtoToDomain
  ),
});

// A state this client does not know yet shows no coverage rather than a
// guessed one.
export const inboxCoverageTransformFromDtoToDomain = (
  data: InboxCoverageDTO | null | undefined
): InboxCoverage | null =>
  data && isInboxCoverageState(data.state)
    ? {
        state: data.state,
        eligibleMemberCount: data.eligible_member_count,
        reachableMemberCount: data.reachable_member_count,
      }
    : null;

export const managedInboxTransformFromDtoToDomain = (
  data: ManagedInboxDTO
): ManagedInboxDomain =>
  new ManagedInboxDomain(
    data.id,
    data.key,
    data.name,
    data.is_active,
    data.purpose,
    data.version,
    data.archived_at,
    data.permitted_actions.rename,
    data.permitted_actions.edit_purpose,
    data.permitted_actions.archive,
    data.archive_blockers.map(inboxArchiveBlockerTransformFromDtoToDomain),
    data.close_agent_policy,
    data.close_inbox_policy,
    data.permitted_actions.edit_close_agent_policy,
    data.permitted_actions.edit_close_inbox_policy,
    // Absent (no members API) stays distinct from false (archived).
    typeof data.permitted_actions.edit_members === "boolean"
      ? data.permitted_actions.edit_members
      : null,
    inboxCoverageTransformFromDtoToDomain(data.coverage),
    data.template_scope ?? null,
    typeof data.permitted_actions.edit_templates === "boolean"
      ? data.permitted_actions.edit_templates
      : null
  );

export const inboxGrantTransformFromDtoToDomain = (
  data: InboxGrantDTO
): InboxGrant => ({
  id: data.id,
  agentId: data.agent_id,
  agentName: data.agent_name,
  source: data.source,
  createdAt: data.created_at,
  createdByAgentId: data.created_by_agent_id,
  conversationScope: isConversationScope(data.conversation_scope)
    ? data.conversation_scope
    : null,
});

export const inboxPersonTransformFromDtoToDomain = (
  data: InboxPersonDTO
): InboxPerson => ({
  agentId: data.agent_id,
  name: data.name,
  isEligibleForUnassignedAlerts: data.eligible_for_unassigned_alerts,
  access: inboxAccessTransformFromDtoToDomain(data.access),
});

export const inboxMembersTransformFromDtoToDomain = (
  data: InboxMembersDTO
): InboxMembersDomain =>
  new InboxMembersDomain(
    data.inbox_id,
    data.version,
    // Absent is initialized (see InboxMembersDTO).
    data.grants_initialized !== false,
    data.grants.map(inboxGrantTransformFromDtoToDomain),
    data.people.map(inboxPersonTransformFromDtoToDomain),
    inboxCoverageTransformFromDtoToDomain(data.coverage),
    // Only an explicit true enables scope editing.
    data.access_editing_enabled === true
  );
